import { useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { Loader2, ArrowRight, Mail, CheckCircle2 } from "lucide-react";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email: email.trim() });
      setSent(true);
    } catch (err) {
      setError(formatApiError(err?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 bg-slate-50">
      <div className="w-full max-w-sm bg-white border border-slate-200 p-8" data-testid="forgot-page">
        <div className="flex items-center gap-2.5 mb-6">
          <img src="/vyntric-favicon.png" alt="Vyntric" className="w-9 h-9 object-contain" />
          <div className="leading-tight">
            <div className="text-sm font-black tracking-tight">Vyntric</div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500">Recuperação de acesso</div>
          </div>
        </div>

        {!sent ? (
          <>
            <h1 className="text-2xl font-black tracking-tighter">Esqueceu a senha?</h1>
            <p className="text-sm text-slate-600 mt-1 mb-6">
              Informe seu e-mail cadastrado. Enviaremos um link seguro para redefinir sua senha.
            </p>
            <form onSubmit={onSubmit} data-testid="forgot-form">
              <label className="block text-[10px] uppercase tracking-widest font-bold text-slate-700">
                E-mail
              </label>
              <input
                data-testid="forgot-email"
                type="email"
                required
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1 w-full border border-slate-300 rounded-sm px-3 py-2.5 text-sm focus:outline-none focus:border-[#002EA6] focus:ring-1 focus:ring-[#002EA6]"
                placeholder="seu@email.com"
              />
              {error && (
                <div data-testid="forgot-error" className="mt-4 px-3 py-2 text-xs bg-red-50 text-red-800 border border-red-200">
                  {error}
                </div>
              )}
              <button
                data-testid="forgot-submit"
                type="submit"
                disabled={loading || !email.trim()}
                className="mt-5 w-full bg-[#002EA6] hover:bg-[#00207A] text-white font-bold text-sm tracking-wide py-3 flex items-center justify-center gap-2 transition-colors disabled:opacity-60"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
                Enviar link de recuperação
              </button>
            </form>
          </>
        ) : (
          <div data-testid="forgot-success">
            <div className="w-12 h-12 bg-emerald-50 border border-emerald-200 flex items-center justify-center mb-4">
              <CheckCircle2 className="w-6 h-6 text-emerald-600" />
            </div>
            <h1 className="text-2xl font-black tracking-tighter">Verifique seu e-mail</h1>
            <p className="text-sm text-slate-600 mt-2 leading-relaxed">
              Se o endereço <strong className="font-mono">{email}</strong> estiver cadastrado, você receberá
              em instantes uma mensagem com o link de redefinição.
            </p>
            <p className="text-xs text-slate-500 mt-4 leading-relaxed">
              Não recebeu? Confira o SPAM/lixo eletrônico ou tente novamente em alguns minutos —
              o link é válido por 60 minutos.
            </p>
          </div>
        )}

        <div className="mt-6 pt-4 border-t border-slate-100 text-center text-xs text-slate-500">
          <Link
            to="/login"
            data-testid="forgot-back-login"
            className="text-[#002EA6] font-bold hover:underline inline-flex items-center gap-1"
          >
            <ArrowRight className="w-3 h-3 rotate-180" /> Voltar para o login
          </Link>
        </div>
      </div>
    </div>
  );
}
